import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { Badge, EmptyRow, LEVEL_LABELS, PanelTitle, StatCard, Table, fmtDate, money, shortHash } from "./ui";

const LEVEL_AMOUNTS = { 1: 25, 2: 10, 3: 5, 4: 5, 5: 50 };

export const CommissionsTable = ({ items, loading, showBeneficiary, actions, testId = "commissions-table" }) => {
  const [status, setStatus] = useState("all");
  const rows = items.filter((c) => status === "all" || c.status === status);
  const headers = [...(showBeneficiary ? ["Beneficiario"] : []), "Nivel", "Origen (membresía pagada)", "Monto", "Estado", "Generada", "Pagada el", "Hash de pago", ...(actions ? [""] : [])];
  return (
    <>
      <div className="mt-6 flex flex-wrap gap-2">
        {[["all", "Todas"], ["pendiente", "Pendientes"], ["pagada", "Pagadas"]].map(([v, l]) => (
          <button key={v} onClick={() => setStatus(v)} className={`rounded-full px-3.5 py-2 text-xs font-semibold transition-colors ${status === v ? "bg-ink text-white" : "border border-ink/15 bg-white hover:bg-paper"}`} data-testid={`commissions-filter-${v}`}>
            {l} ({items.filter((c) => v === "all" || c.status === v).length})
          </button>
        ))}
      </div>
      <Table headers={headers} testId={testId} minWidth="min-w-[960px]">
        {loading && <tr><td colSpan={headers.length} className="px-4 py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>}
        {!loading && !rows.length && <EmptyRow cols={headers.length} text="Sin comisiones en esta vista. Se generan cuando alguien de tu red paga su Membresía Dorada." testId="commissions-empty" />}
        {rows.map((c, i) => (
          <tr key={c.id} className="border-b border-ink/5 last:border-0" data-testid={`commission-row-${i}`}>
            {showBeneficiary && <td className="px-4 py-3"><p className="font-semibold">{c.beneficiary_name}</p><p className="font-mono text-xs text-ink/60">{c.beneficiary_id}{!c.payable && " · sin membresía"}</p></td>}
            <td className="px-4 py-3"><span className="rounded-full bg-paper px-2.5 py-1 font-mono text-xs">{LEVEL_LABELS[c.level]}</span></td>
            <td className="px-4 py-3"><p className="font-semibold">{c.source_name}</p><p className="font-mono text-xs text-ink/60">ID {c.source_id}</p></td>
            <td className="px-4 py-3 font-display text-base font-semibold">{money(c.amount)}</td>
            <td className="px-4 py-3"><Badge status={c.status} label={c.status === "pagada" ? "Cobrada" : "Pendiente"} /></td>
            <td className="whitespace-nowrap px-4 py-3 text-ink/70">{fmtDate(c.created_at)}</td>
            <td className="whitespace-nowrap px-4 py-3 text-ink/70">{fmtDate(c.paid_at)}</td>
            <td className="px-4 py-3 font-mono text-xs" title={c.paid_tx_hash}>{shortHash(c.paid_tx_hash)}</td>
            {actions && <td className="px-4 py-3">{actions(c)}</td>}
          </tr>
        ))}
      </Table>
    </>
  );
};

export const LevelSummary = ({ byLevel }) => (
  <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-5" data-testid="earnings-by-level">
    {Object.entries(byLevel).map(([lvl, b]) => (
      <div key={lvl} className="rounded-2xl border border-ink/10 bg-white p-4" data-testid={`earnings-level-${lvl}`}>
        <p className="font-mono text-[11px] uppercase tracking-[0.1em] text-ink/60">{LEVEL_LABELS[lvl]} · {money(LEVEL_AMOUNTS[lvl])}</p>
        <p className="mt-2 font-display text-2xl font-semibold">{money(b.amount)}</p>
        <p className="mt-1 text-xs text-ink/60">{b.count} membresías · pendiente {money(b.pending)}</p>
      </div>
    ))}
  </div>
);

export const EarningsPanel = ({ active }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/me/commissions").then(({ data }) => setItems(data)).finally(() => setLoading(false));
  }, []);
  const total = items.reduce((a, c) => a + c.amount, 0);
  const paid = items.filter((c) => c.status === "pagada").reduce((a, c) => a + c.amount, 0);
  const byLevel = Object.fromEntries(Object.keys(LEVEL_AMOUNTS).map((l) => [l, { count: 0, amount: 0, pending: 0 }]));
  items.forEach((c) => {
    const b = byLevel[c.level];
    b.count += 1;
    b.amount += c.amount;
    if (c.status === "pendiente") b.pending += c.amount;
  });
  return (
    <div data-testid="earnings-panel">
      <PanelTitle title="Mis ganancias" text="Cada membresía realmente pagada en tu red genera una comisión única según el nivel: $25 · $10 · $5 · $5 · $50. Aquí ves cuáles ya cobraste y cuáles están pendientes, con la fecha en que el administrador las pagó." />
      {!active && <p className="mt-4 rounded-xl border border-sun/60 bg-sun/15 px-4 py-3 text-sm" data-testid="earnings-inactive-note">Tus comisiones se acumulan, pero solo podrán pagarse cuando tu propia Membresía Dorada esté activa.</p>}
      <div className="mt-6 grid grid-cols-3 gap-3">
        <StatCard dark label="Total generado" value={money(total)} testId="earnings-total" />
        <StatCard label="Cobrado" value={money(paid)} testId="earnings-paid" />
        <StatCard label="Pendiente" value={money(total - paid)} testId="earnings-pending" />
      </div>
      <LevelSummary byLevel={byLevel} />
      <CommissionsTable items={items} loading={loading} />
    </div>
  );
};
